import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Loader2, Trash2, X, Check, MapPin, ChevronRight, Phone, Mail, ArrowLeft, PoundSterling, Map, List, Edit2, MoreVertical, Calendar, Route } from 'lucide-react';
import { subscribeToRefresh, roundChanged, Actions } from '../utils/dataSync';
import { zoneColors } from '../data/csvData';
import { refreshRoundColors } from '../utils/roundColors';
import JobDetailPanel from '../components/JobDetailPanel';
import InvoiceSummaryModal from '../components/InvoiceSummaryModal';
import { toast } from 'sonner';
import RoundReplanModal from '../components/RoundReplanModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default color palette for new rounds - including dark yellow
const ROUND_COLORS = [
  '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#f97316',
  '#14b8a6', '#06b6d4', '#6366f1', '#84cc16', '#f59e0b',
  '#ef4444', '#a855f7', '#0ea5e9', '#22c55e', '#e11d48',
  '#ca8a04', '#b45309', '#eab308'  // Dark yellows/ambers added
];

// Get color for a round - prioritize user-saved color, then zoneColors mapping, then default
const getRoundColor = (round) => {
  // First use stored/saved color if available (user's choice)
  if (round.color) {
    return round.color;
  }
  // Then check if zoneColors has this round name (Squeegee defaults)
  if (round.name && zoneColors[round.name]) {
    return zoneColors[round.name];
  }
  // Finally fall back to hash-based color
  const colorIndex = round.name ? round.name.charCodeAt(0) % ROUND_COLORS.length : 0;
  return ROUND_COLORS[colorIndex];
};

// Get initials for zone avatar
const getZoneInitials = (zoneName) => {
  if (!zoneName) return '?';
  const words = zoneName.split(' ');
  if (words.length >= 2) {
    const first = words[0][0];
    const last = words[words.length - 1];
    if (!isNaN(last)) {
      return first + last;
    }
    return (first + last[0]).toUpperCase();
  }
  return zoneName.substring(0, 2).toUpperCase();
};

// Format a "Next due" pill for a job's scheduled_date.
// Returns { label, className } or null if no date.
const formatNextDue = (isoDate) => {
  if (!isoDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(isoDate + 'T00:00:00');
  if (Number.isNaN(due.getTime())) return null;
  const diffDays = Math.round((due - today) / 86400000);

  const fmt = due.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  let label;
  if (diffDays < 0) label = `Overdue · ${fmt}`;
  else if (diffDays === 0) label = `Today · ${fmt}`;
  else if (diffDays === 1) label = `Tomorrow · ${fmt}`;
  else if (diffDays <= 7) label = `In ${diffDays}d · ${fmt}`;
  else label = fmt;

  let className = 'bg-gray-100 text-gray-600';
  if (diffDays < 0) className = 'bg-red-50 text-red-600';
  else if (diffDays === 0) className = 'bg-orange-50 text-orange-600';
  else if (diffDays <= 7) className = 'bg-blue-50 text-blue-600';

  return { label, className };
};

// ============================================
// ROUND ROW - Squeegee Style
// ============================================
const RoundRow = ({ round, onClick, showAdhocOnly, onEdit }) => {
  // Use getRoundColor for consistent Squeegee-matching colors
  const color = getRoundColor(round);
  
  // Use live calculated values from backend
  const monthlyValue = round.monthly_revenue || round.monthly_value || 0;
  const annualValue = round.annual_revenue || round.annual_value || 0;  // Recurring only
  const adhocTotal = round.adhoc_total || 0;  // All ad-hoc jobs
  const adhocThisYear = round.adhoc_this_year || 0;  // Ad-hoc done this year
  const activeJobs = round.customer_count || 0;
  const jobCount = round.job_count || 0;
  const adhocJobCount = round.adhoc_job_count || 0;
  
  return (
    <div 
      data-testid={`round-row-${round.name}`}
      className="bg-white border-b border-gray-100 active:bg-gray-50 transition-colors"
      onClick={() => onClick(round)}
    >
      <div className="flex items-center min-w-max">
        {/* Fixed: Round Name and Active Jobs */}
        <div className="flex items-center gap-2 sm:gap-3 w-40 sm:w-56 flex-shrink-0 px-3 sm:px-4 py-4 sticky left-0 bg-white z-10 border-r border-gray-100">
          <div 
            className="w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-bold flex-shrink-0 shadow-sm"
            style={{ backgroundColor: color }}
          >
            {getZoneInitials(round.name)}
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 truncate text-sm sm:text-base">{round.name}</h3>
            <p className="text-xs sm:text-sm text-gray-500 truncate">
              {showAdhocOnly 
                ? `${adhocJobCount} ad-hoc jobs`
                : `${activeJobs} cust · ${jobCount} jobs`
              }
            </p>
          </div>
        </div>
        
        {showAdhocOnly ? (
          /* Ad-hoc only view - show ad-hoc done this year */
          <div className="w-32 sm:w-40 flex-shrink-0 px-3 sm:px-4 py-4 text-right">
            <p className="text-sm sm:text-base font-semibold text-amber-600">£{adhocThisYear.toFixed(2)}</p>
            <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5">Done This Year</p>
          </div>
        ) : (
          <>
            {/* Scrollable: Monthly Recurring Column */}
            <div className="w-24 sm:w-36 flex-shrink-0 px-2 sm:px-4 py-4 text-right">
              <p className="text-sm sm:text-base font-semibold text-emerald-600">£{monthlyValue.toFixed(2)}</p>
              <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5">Avg Monthly</p>
            </div>
            
            {/* Scrollable: Annual Recurring Column */}
            <div className="w-28 sm:w-40 flex-shrink-0 px-2 sm:px-4 py-4 text-right">
              <p className="text-sm sm:text-base font-semibold text-blue-600">£{annualValue.toFixed(2)}</p>
              <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5">Annual</p>
            </div>
            
            {/* Ad-hoc Total Column - Always show for alignment */}
            <div className="w-24 sm:w-36 flex-shrink-0 px-2 sm:px-4 py-4 text-right">
              <p className={`text-sm sm:text-base font-semibold ${adhocTotal > 0 ? 'text-amber-600' : 'text-gray-300'}`}>
                £{adhocTotal.toFixed(2)}
              </p>
              <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5">Ad-hoc</p>
            </div>
          </>
        )}
        
        {/* Edit Button */}
        <div className="w-8 sm:w-12 flex-shrink-0 px-1 sm:px-2 flex items-center justify-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(round);
            }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Edit Round"
          >
            <Edit2 size={18} className="text-gray-400 hover:text-blue-500" />
          </button>
        </div>
        
        {/* Arrow */}
        <div className="w-8 flex-shrink-0 flex items-center justify-center">
          <ChevronRight size={20} className="text-gray-300" />
        </div>
      </div>
    </div>
  );
};

// ============================================
// ADD ROUND MODAL
// ============================================
const AddRoundModal = ({ isOpen, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [selectedColor, setSelectedColor] = useState(ROUND_COLORS[0]);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    
    setIsSaving(true);
    try {
      await onSave({ name: name.trim(), color: selectedColor });
      setName('');
      setSelectedColor(ROUND_COLORS[0]);
      onClose();
    } catch (err) {
      console.error('Error saving round:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center" onClick={onClose}>
      <div 
        className="bg-white rounded-t-2xl w-full max-w-lg p-6 animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Add New Round</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Round Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Monday Route, Zone A"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
            <div className="flex flex-wrap gap-2">
              {ROUND_COLORS.map(color => (
                <button
                  key={color}
                  onClick={() => setSelectedColor(color)}
                  className={`w-10 h-10 rounded-full transition-transform ${
                    selectedColor === color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
          
          <button
            onClick={handleSave}
            disabled={!name.trim() || isSaving}
            className="w-full py-4 bg-emerald-500 text-white font-semibold rounded-xl hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSaving ? <Loader2 size={20} className="animate-spin" /> : <Check size={20} />}
            Save Round
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// EDIT ROUND MODAL
// ============================================
const EditRoundModal = ({ round, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [selectedColor, setSelectedColor] = useState(ROUND_COLORS[0]);
  const [isSaving, setIsSaving] = useState(false);

  // Update state when round changes
  useEffect(() => {
    if (round) {
      setName(round.name || '');
      setSelectedColor(round.color || getRoundColor(round) || ROUND_COLORS[0]);
    }
  }, [round]);

  const handleSave = async () => {
    if (!name.trim()) return;
    
    setIsSaving(true);
    try {
      await onSave({ ...round, name: name.trim(), color: selectedColor });
      onClose();
    } catch (err) {
      console.error('Error saving round:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (!round) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-end justify-center" onClick={onClose}>
      <div 
        className="bg-white rounded-t-2xl w-full max-w-lg p-6 animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Edit Round</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Round Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Monday Route, Zone A"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
            <div className="flex flex-wrap gap-2">
              {ROUND_COLORS.map(color => (
                <button
                  key={color}
                  onClick={() => setSelectedColor(color)}
                  className={`w-10 h-10 rounded-full transition-transform ${
                    selectedColor === color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
          
          <button
            onClick={handleSave}
            disabled={!name.trim() || isSaving}
            className="w-full py-4 bg-emerald-500 text-white font-semibold rounded-xl hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSaving ? <Loader2 size={20} className="animate-spin" /> : <Check size={20} />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// ROUND CUSTOMERS PANEL
// ============================================
const RoundCustomersPanel = ({ round, onClose, companyLogo, onEditRound, onDeleteRound }) => {
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showReplan, setShowReplan] = useState(false);
  const [topOffset, setTopOffset] = useState(0);

  // Start below the sticky app header so the round title bar stays readable.
  useEffect(() => {
    const measure = () => {
      const el = document.querySelector('header');
      setTopOffset(el ? Math.ceil(el.getBoundingClientRect().bottom) : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const [selectedJob, setSelectedJob] = useState(null);
  const [invoiceForModal, setInvoiceForModal] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [staffList, setStaffList] = useState([]);
  const [roundsList, setRoundsList] = useState([]);
  const [userSettings, setUserSettings] = useState({});
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  
  const totalValue = useMemo(() => customers.reduce((sum, c) => sum + (c.job_price || 0), 0), [customers]);
  const avgPrice = useMemo(() => customers.length > 0 ? totalValue / customers.length : 0, [customers, totalValue]);
  const customersWithCoords = useMemo(() => customers.filter(c => c.latitude && c.longitude), [customers]);
  
  // Load Google Maps
  useEffect(() => {
    if (window.google?.maps) {
      setMapLoaded(true);
      return;
    }
    
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const checkLoaded = setInterval(() => {
        if (window.google?.maps) {
          setMapLoaded(true);
          clearInterval(checkLoaded);
        }
      }, 100);
      return () => clearInterval(checkLoaded);
    }
    
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapLoaded(true);
    document.head.appendChild(script);
  }, []);
  
  // Initialize map when showing
  useEffect(() => {
    if (!showMap || !mapLoaded || !mapRef.current || customersWithCoords.length === 0 || !window.google?.maps) return;
    
    // Clean up existing map is handled by Google Maps garbage collection
    
    const avgLat = customersWithCoords.reduce((sum, c) => sum + c.latitude, 0) / customersWithCoords.length;
    const avgLng = customersWithCoords.reduce((sum, c) => sum + c.longitude, 0) / customersWithCoords.length;
    
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: avgLat, lng: avgLng },
      zoom: 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }
      ]
    });
    
    mapInstanceRef.current = map;
    const infoWindow = new window.google.maps.InfoWindow();
    
    const color = round ? getRoundColor(round) : ROUND_COLORS[0];
    const bounds = new window.google.maps.LatLngBounds();
    const markers = [];
    
    customersWithCoords.forEach(customer => {
      const marker = new window.google.maps.Marker({
        position: { lat: customer.latitude, lng: customer.longitude },
        map: map,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 3,
        }
      });
      
      marker.addListener('click', () => {
        infoWindow.setContent(`
          <div style="min-width: 180px; font-family: system-ui, sans-serif; padding: 4px;">
            <div style="font-weight: 600; font-size: 14px;">${customer.name}</div>
            <div style="font-size: 12px; color: #666; margin: 4px 0;">${customer.address || ''}</div>
            <div style="font-weight: 600; color: #10b981;">£${(customer.job_price || 0).toFixed(2)}</div>
          </div>
        `);
        infoWindow.open(map, marker);
      });
      
      markers.push(marker);
      bounds.extend({ lat: customer.latitude, lng: customer.longitude });
    });
    
    if (customersWithCoords.length > 0) {
      map.fitBounds(bounds, 30);
    }
    
    return () => {
      markers.forEach(m => m.setMap(null));
      mapInstanceRef.current = null;
    };
  }, [showMap, mapLoaded, customersWithCoords, round, companyLogo]);
  
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('streakfree_token');
        
        const custResponse = await fetch(`${API_URL}/api/customers?zone=${encodeURIComponent(round.name)}&limit=500`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const jobsResponse = await fetch(`${API_URL}/api/jobs?limit=5000`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        // Fetch staff list, rounds list and settings so the slide-in
        // JobDetailPanel has the same context as the Work Planner.
        const [staffRes, roundsRes, settingsRes] = await Promise.all([
          fetch(`${API_URL}/api/staff?limit=200`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => null),
          fetch(`${API_URL}/api/rounds?limit=200`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => null),
          fetch(`${API_URL}/api/settings`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => null),
        ]);
        if (staffRes && staffRes.ok) setStaffList(await staffRes.json());
        if (roundsRes && roundsRes.ok) setRoundsList(await roundsRes.json());
        let settingsData = null;
        if (settingsRes && settingsRes.ok) { settingsData = await settingsRes.json(); setUserSettings(settingsData); }
        
        let customersData = [];
        let jobsData = [];
        
        if (custResponse.ok) customersData = await custResponse.json();
        if (jobsResponse.ok) jobsData = await jobsResponse.json();
        
        // Build a per-customer map containing:
        //  - bestJob: the most-relevant job (next scheduled if available, else
        //    fallback to the highest-priced one) used to open JobDetailPanel
        //  - price / services / frequency used for the row summary
        const todayIso = new Date().toISOString().slice(0, 10);
        const customerJobMap = {};
        jobsData.forEach(job => {
          const custId = job.customer_id;
          if (!custId) return;
          const existing = customerJobMap[custId];
          const isScheduled = job.status === 'scheduled';
          const isFuture = job.scheduled_date && job.scheduled_date >= todayIso;

          if (!existing) {
            customerJobMap[custId] = { bestJob: job };
          } else {
            const exJob = existing.bestJob;
            const exScheduled = exJob.status === 'scheduled';
            const exFuture = exJob.scheduled_date && exJob.scheduled_date >= todayIso;
            // Prefer upcoming scheduled jobs first
            if (isScheduled && isFuture && !(exScheduled && exFuture)) {
              customerJobMap[custId] = { bestJob: job };
            } else if (isScheduled && isFuture && exScheduled && exFuture) {
              // Both upcoming – pick the soonest
              if (job.scheduled_date < exJob.scheduled_date) {
                customerJobMap[custId] = { bestJob: job };
              }
            } else if (!(exScheduled && exFuture)) {
              // Otherwise prefer the higher-priced job (legacy behaviour)
              if ((job.price || 0) > (exJob.price || 0)) {
                customerJobMap[custId] = { bestJob: job };
              }
            }
          }
        });
        
        const customersWithJobs = customersData.map(c => {
          const bestJob = customerJobMap[c.id]?.bestJob || null;
          return {
            ...c,
            job: bestJob,
            job_price: bestJob?.price || 0,
            job_services: bestJob?.services || '',
            job_frequency: bestJob?.frequency || ''
          };
        });
        
        // Standard order: distance from the business base postcode, so new
        // customers automatically slot into their optimised position.
        const baseLoc = settingsData?.base_location;
        if (baseLoc && baseLoc.lat != null) {
          const distMi = (c) => {
            const g = c.geolocation || {};
            if (g.lat == null || g.lng == null) return null;
            const dLat = (g.lat - baseLoc.lat) * Math.PI / 180;
            const dLng = (g.lng - baseLoc.lng) * Math.PI / 180;
            const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(baseLoc.lat * Math.PI / 180) * Math.cos(g.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
            return 3958.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          };
          customersWithJobs.forEach(c => {
            const d = distMi(c);
            c.distance_mi = d == null ? null : Math.round(d * 10) / 10;
          });
          customersWithJobs.sort((a, b) =>
            ((a.distance_mi ?? 1e9) - (b.distance_mi ?? 1e9)) || (a.name || '').localeCompare(b.name || ''));
        } else {
          customersWithJobs.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        }
        setCustomers(customersWithJobs);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setIsLoading(false);
      }
    };
    if (round) fetchData();
  }, [round, refreshTick]);
  
  const color = round ? getRoundColor(round) : ROUND_COLORS[0];

  return (
    <div className="fixed inset-x-0 bottom-0 bg-white z-50 flex flex-col" style={{ top: topOffset }} data-testid="round-detail-panel">
      {/* Header */}
      <div className="bg-zinc-800 text-white px-4 py-4 flex items-center gap-3 flex-shrink-0" data-testid="round-detail-header">
        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
          <ArrowLeft size={20} />
        </button>
        <div 
          className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
          style={{ backgroundColor: color }}
        >
          {getZoneInitials(round?.name)}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-semibold text-lg truncate">{round?.name}</h1>
          <p className="text-zinc-400 text-sm">{customers.length} customers • {customersWithCoords.length} on map</p>
        </div>
        {/* Re-plan */}
        <button
          onClick={() => setShowReplan(true)}
          data-testid="round-replan-btn"
          className="p-2 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors"
          title="Re-plan this round"
        >
          <Route size={18} />
        </button>
        {/* Map/List Toggle */}
        <div className="flex bg-white/10 rounded-lg p-1">
          <button
            onClick={() => setShowMap(false)}
            className={`p-2 rounded-md transition-colors ${!showMap ? 'bg-white text-zinc-800' : 'text-white hover:bg-white/10'}`}
            title="List View"
          >
            <List size={18} />
          </button>
          <button
            onClick={() => setShowMap(true)}
            className={`p-2 rounded-md transition-colors ${showMap ? 'bg-white text-zinc-800' : 'text-white hover:bg-white/10'}`}
            title="Map View"
          >
            <Map size={18} />
          </button>
        </div>
        {/* Menu */}
        <div className="relative">
          <button 
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-white/10 rounded-lg"
          >
            <MoreVertical size={20} />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl z-50 min-w-[150px] overflow-hidden">
              <button
                onClick={() => {
                  setShowMenu(false);
                  onEditRound(round);
                }}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 text-left text-gray-700"
              >
                <Edit2 size={18} className="text-blue-500" />
                <span>Edit Round</span>
              </button>
              <button
                onClick={() => {
                  setShowMenu(false);
                  onDeleteRound(round);
                }}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 text-left text-red-600"
              >
                <Trash2 size={18} />
                <span>Delete Round</span>
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Summary */}
      <div className="bg-emerald-50 px-4 py-4 border-b border-emerald-100 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-emerald-600">Total per clean</p>
            <p className="text-2xl font-bold text-emerald-700">£{totalValue.toFixed(2)}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-emerald-600">Average</p>
            <p className="text-xl font-semibold text-emerald-700">£{avgPrice.toFixed(2)}</p>
          </div>
        </div>
      </div>
      
      {/* Customer List or Map View */}
      <div className="flex-1 overflow-hidden">
        {showMap ? (
          /* Map View */
          <div className="h-full relative">
            {isLoading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-zinc-100">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : customersWithCoords.length === 0 ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-50">
                <MapPin size={48} className="text-zinc-300 mb-4" />
                <p className="text-zinc-500 font-medium">No customer locations found</p>
                <p className="text-zinc-400 text-sm mt-1">Add addresses with coordinates to see them on the map</p>
              </div>
            ) : (
              <div ref={mapRef} className="w-full h-full" />
            )}
          </div>
        ) : (
          /* List View */
          <div className="h-full overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : customers.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                <MapPin size={32} className="mb-2" />
                <p>No customers in this round</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {customers.map(customer => (
                  <div
                    key={customer.id}
                    data-testid={`round-customer-row-${customer.id}`}
                    className="px-4 py-4 hover:bg-gray-50 cursor-pointer active:bg-gray-100"
                    onClick={() => customer.job && setSelectedJob(customer.job)}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-11 h-11 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                        style={{ backgroundColor: color }}
                      >
                        {customer.name?.charAt(0) || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-gray-900 truncate">{customer.name}</h3>
                          <div className="flex flex-col items-end flex-shrink-0">
                            <span className="font-bold text-emerald-600 text-lg leading-tight">
                              £{(customer.job_price || 0).toFixed(2)}
                            </span>
                            {(() => {
                              const pill = formatNextDue(customer.job?.scheduled_date);
                              if (!pill) return null;
                              return (
                                <span
                                  data-testid={`next-due-pill-${customer.id}`}
                                  className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${pill.className}`}
                                  title="Next scheduled visit"
                                >
                                  <Calendar size={11} />
                                  {pill.label}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                        <p className="text-sm text-gray-500 truncate">
                          {customer.distance_mi != null && (
                            <span className="text-indigo-600 font-semibold" data-testid={`distance-${customer.id}`}>{customer.distance_mi} mi · </span>
                          )}
                          {customer.address}
                        </p>
                        {customer.job_services && (
                          <p className="text-xs text-gray-400 mt-1">{customer.job_services}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3 ml-14">
                      {customer.phone && (
                        <a 
                          href={`tel:${customer.phone}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-full text-sm font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Phone size={14} /> Call
                        </a>
                      )}
                      {customer.email && (
                        <a 
                          href={`mailto:${customer.email}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Mail size={14} /> Email
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Round Re-plan (Phase 1, read-only) */}
      {showReplan && (
        <RoundReplanModal
          initialRound={round?.name}
          allRounds={(roundsList || []).map(r => r.name).filter(Boolean)}
          onClose={() => setShowReplan(false)}
        />
      )}

      {/* Job Detail Panel — opens when a customer is clicked, mirrors WorkPlanner UX */}
      <JobDetailPanel
        job={selectedJob}
        isOpen={!!selectedJob}
        onClose={() => setSelectedJob(null)}
        onComplete={async (job, options = {}) => {
          try {
            const token = localStorage.getItem('streakfree_token');
            const body = {
              send_notification: options.sendMethod && options.sendMethod !== 'none',
              send_method: options.sendMethod || 'none',
              skip_auto_charge: !!options.skipAutoCharge,
              occurrence_date: job.scheduled_date || null,
            };
            if (options.chargeAmount != null) body.charge_amount = options.chargeAmount;
            if (options.paymentType) body.payment_type = options.paymentType;
            const response = await fetch(`${API_URL}/api/jobs/${job.id}/complete`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            });
            if (response.ok) {
              setSelectedJob(null);
              // Trigger Rounds refresh via central data sync
              localStorage.setItem('lastJobUpdate', Date.now().toString());
            }
          } catch (err) {
            console.error('Error completing job from Rounds:', err);
          }
        }}
        onDoneAndPaid={async (job, options = {}) => {
          try {
            const token = localStorage.getItem('streakfree_token');
            const response = await fetch(`${API_URL}/api/jobs/${job.id}/complete`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({
                mark_paid: true,
                payment_method: options.paymentMethod || 'cash',
                send_notification: false,
                occurrence_date: job.scheduled_date || null,
              }),
            });
            if (response.ok) {
              setSelectedJob(null);
              localStorage.setItem('lastJobUpdate', Date.now().toString());
            }
          } catch (err) {
            console.error('Error marking paid from Rounds:', err);
          }
        }}
        onSkip={async (job, reason) => {
          try {
            const token = localStorage.getItem('streakfree_token');
            const params = new URLSearchParams({ reason: reason || 'Skipped' });
            if (job.scheduled_date) params.set('occurrence_date', job.scheduled_date);
            const response = await fetch(
              `${API_URL}/api/jobs/${job.id}/skip?${params.toString()}`,
              { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
              setSelectedJob(null);
              localStorage.setItem('lastJobUpdate', Date.now().toString());
            }
          } catch (err) {
            console.error('Error skipping job from Rounds:', err);
          }
        }}
        onReschedule={() => {}}
        onNavigate={(job) => {
          if (!job) return;
          const addr = job.address || job.customer_address || '';
          if (addr) {
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`, '_blank');
          }
        }}
        onViewCustomer={() => {}}
        onUndoComplete={async (job) => {
          try {
            const token = localStorage.getItem('streakfree_token');
            await fetch(`${API_URL}/api/jobs/${job.id}/undo-complete`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
            });
            setSelectedJob(null);
            localStorage.setItem('lastJobUpdate', Date.now().toString());
          } catch (err) {
            console.error('Error undoing complete:', err);
          }
        }}
        onViewInvoice={(inv) => setInvoiceForModal(inv)}
        onUpdateJob={async (jobId, updates) => {
          try {
            const token = localStorage.getItem('streakfree_token');
            // Round lives on the CUSTOMER (zone) — intercept like WorkPlanner
            // does, then let the backend cascade it to the open jobs.
            if (updates._customer_zone !== undefined) {
              const custId = selectedJob?.customer_id;
              if (custId) {
                await fetch(`${API_URL}/api/customers/${custId}`, {
                  method: 'PATCH',
                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ zone: updates._customer_zone }),
                });
              }
              updates = { ...updates };
              delete updates._customer_zone;
              delete updates.round_name;
              if (!Object.keys(updates).length) {
                setSelectedJob(prev => prev ? { ...prev, zone: undefined } : prev);
                setRefreshTick(t => t + 1);
                localStorage.setItem('lastJobUpdate', Date.now().toString());
                return;
              }
            }
            const response = await fetch(`${API_URL}/api/jobs/${jobId}`, {
              method: 'PATCH',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify(updates),
            });
            if (response.ok) {
              const updated = await response.json();
              setSelectedJob(prev => prev ? { ...prev, ...updated } : prev);
              setRefreshTick(t => t + 1);
              localStorage.setItem('lastJobUpdate', Date.now().toString());
            }
          } catch (err) {
            console.error('Error updating job from Rounds:', err);
          }
        }}
        getStaffColor={() => '#10b981'}
        staffList={staffList}
        roundsList={roundsList}
        userSettings={userSettings}
      />
      {invoiceForModal && (
        <InvoiceSummaryModal
          invoice={invoiceForModal}
          zIndex={116}
          onClose={() => setInvoiceForModal(null)}
          onSendInvoice={async (invoice, method) => {
            try {
              const token = localStorage.getItem('streakfree_token');
              const response = await fetch(`${API_URL}/api/invoices/${invoice.id}/send?method=${method}&copy_to_owner=true`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
              });
              if (response.ok) {
                toast.success(`Invoice sent via ${method} (copy to you)`);
              } else {
                const err = await response.json();
                toast.error(err.detail || `Failed to send ${method}`);
              }
            } catch (err) {
              toast.error(`Failed to send invoice via ${method}`);
            }
          }}
        />
      )}
    </div>
  );
};

// ============================================
// MAIN ROUNDS COMPONENT - Squeegee Style
// ============================================
const Rounds = () => {
  const [rounds, setRounds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedRound, setSelectedRound] = useState(null);
  const [editingRound, setEditingRound] = useState(null);  // For edit modal
  const [showAdhocOnly, setShowAdhocOnly] = useState(false); // Filter for ad-hoc jobs only
  const [companyLogo, setCompanyLogo] = useState(null);

  // Load company logo from settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCompanyLogo(data.company_logo || null);
        }
      } catch (err) {
        console.error('Error loading settings:', err);
      }
    };
    loadSettings();
  }, []);

  // Refetch rounds data - can be called after job updates
  const fetchRounds = async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      
      const roundsResponse = await fetch(`${API_URL}/api/rounds?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!roundsResponse.ok) {
        setIsLoading(false);
        return;
      }
      
      const roundsData = await roundsResponse.json();
      
      // Backend already calculates job_count, monthly_revenue, annual_revenue correctly
      // Just use that data directly - no need to recalculate on frontend
      const roundsWithData = roundsData.map(round => ({
        ...round,
        // Use backend-calculated values
        job_count: round.job_count || 0,
        customer_count: round.customer_count || 0,
        monthly_value: round.monthly_revenue || 0,
        annual_value: round.annual_revenue || 0,
        one_off_value: round.adhoc_total || 0,
        adhoc_this_year: round.adhoc_this_year || 0,
        adhoc_job_count: round.adhoc_job_count || 0,
      }));
      
      roundsWithData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setRounds(roundsWithData);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchRounds();
  }, []);

  // Watch for data changes even when page wasn't mounted (uses localStorage as cross-page sync)
  useEffect(() => {
    const checkForUpdates = () => {
      const lastJobUpdate = localStorage.getItem('lastJobUpdate');
      const lastRoundsFetch = localStorage.getItem('lastRoundsFetch');
      
      if (lastJobUpdate && (!lastRoundsFetch || parseInt(lastJobUpdate) > parseInt(lastRoundsFetch))) {
        console.log('[Rounds] Detected job update while page was not mounted, refreshing...');
        localStorage.setItem('lastRoundsFetch', Date.now().toString());
        fetchRounds();
      }
    };
    
    // Check on mount
    checkForUpdates();
    
    // Also check on focus/visibility
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdates();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', checkForUpdates);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', checkForUpdates);
    };
  }, []);

  // Subscribe to centralized data sync - auto refresh when jobs, customers, or rounds change
  useEffect(() => {
    const unsubscribe = subscribeToRefresh('rounds', (detail) => {
      console.log('[Rounds] Refresh triggered by:', detail.source);
      localStorage.setItem('lastRoundsFetch', Date.now().toString());
      fetchRounds();
    });
    return unsubscribe;
  }, []);

  const totals = useMemo(() => {
    return rounds.reduce((acc, r) => ({
      totalCustomers: acc.totalCustomers + (r.customer_count || 0),
      totalJobs: acc.totalJobs + (r.job_count || 0),
      monthlyValue: acc.monthlyValue + (r.monthly_revenue || r.monthly_value || 0),
      annualValue: acc.annualValue + (r.annual_revenue || r.annual_value || 0),  // Recurring only
      adhocTotal: acc.adhocTotal + (r.adhoc_total || 0),  // All ad-hoc
      adhocThisYear: acc.adhocThisYear + (r.adhoc_this_year || 0),  // Done this year
      adhocJobCount: acc.adhocJobCount + (r.adhoc_job_count || 0),
    }), { totalCustomers: 0, totalJobs: 0, monthlyValue: 0, annualValue: 0, adhocTotal: 0, adhocThisYear: 0, adhocJobCount: 0 });
  }, [rounds]);

  const handleAddRound = async (roundData) => {
    const token = localStorage.getItem('streakfree_token');
    const response = await fetch(`${API_URL}/api/rounds`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(roundData)
    });
    
    if (response.ok) {
      const newRound = await response.json();
      roundChanged(Actions.CREATE, newRound);
      setRounds(prev => [...prev, newRound].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    }
  };

  const handleDeleteRound = async (round) => {
    if (!window.confirm(`Delete "${round.name}"? This will remove the round but keep all customers.`)) return;

    const token = localStorage.getItem('streakfree_token');
    try {
      const response = await fetch(`${API_URL}/api/rounds/${round.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        roundChanged(Actions.DELETE, round);
        setRounds(prev => prev.filter(r => r.id !== round.id));
        setSelectedRound(null);  // Close panel if open
      } else {
        let detail = `${response.status}`;
        try { const j = await response.json(); detail = j.detail || JSON.stringify(j); }
        catch { detail = await response.text() || detail; }
        alert(`Could not delete "${round.name}": ${detail}`);
      }
    } catch (e) {
      alert(`Could not delete "${round.name}": ${e.message || e}`);
    }
  };

  const handleEditRound = async (roundData) => {
    const token = localStorage.getItem('streakfree_token');
    const response = await fetch(`${API_URL}/api/rounds/${roundData.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ name: roundData.name, color: roundData.color })
    });
    
    if (response.ok) {
      const updatedRound = await response.json();
      roundChanged(Actions.UPDATE, updatedRound);
      setRounds(prev => prev.map(r => r.id === updatedRound.id ? { ...r, ...updatedRound } : r));
      setEditingRound(null);
      // Update selected round if it was being edited
      if (selectedRound?.id === updatedRound.id) {
        setSelectedRound(prev => ({ ...prev, ...updatedRound }));
      }
      // Propagate the new colour to Work Planner / Job List badges instantly
      refreshRoundColors();
    }
  };

  return (
    <div data-testid="rounds-page" className="h-full flex flex-col bg-gray-50 pb-20 md:pb-0">
      {/* Header */}
      <div className="bg-zinc-800 text-white px-4 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Rounds</h1>
          {/* Ad-hoc Toggle */}
          <button
            onClick={() => setShowAdhocOnly(!showAdhocOnly)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              showAdhocOnly 
                ? 'bg-amber-500 text-white' 
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${showAdhocOnly ? 'bg-white' : 'bg-amber-400'}`} />
            Ad-hoc {new Date().getFullYear()}
          </button>
        </div>
        {showAdhocOnly && (
          <p className="text-amber-300 text-sm mt-2">Showing one-off jobs scheduled in {new Date().getFullYear()} only</p>
        )}
      </div>
      
      {/* Column Headers - Fixed position, scrollable */}
      <div className="bg-gray-100 border-b border-gray-200 flex-shrink-0 overflow-x-auto">
        <div className="flex items-center min-w-max text-xs font-semibold text-gray-500 uppercase tracking-wider">
          <div className="w-40 sm:w-56 flex-shrink-0 px-3 sm:px-4 py-3 sticky left-0 bg-gray-100 z-10">Round</div>
          {showAdhocOnly ? (
            <div className="w-32 sm:w-40 flex-shrink-0 px-3 sm:px-4 py-3 text-right">Ad-hoc This Year</div>
          ) : (
            <>
              <div className="w-24 sm:w-36 flex-shrink-0 px-2 sm:px-4 py-3 text-right">Avg Monthly</div>
              <div className="w-28 sm:w-40 flex-shrink-0 px-2 sm:px-4 py-3 text-right">Annual Revenue</div>
              <div className="w-24 sm:w-36 flex-shrink-0 px-2 sm:px-4 py-3 text-right">Ad-hoc</div>
            </>
          )}
          <div className="w-8 sm:w-12 flex-shrink-0"></div>
        </div>
      </div>
      
      {/* Rounds List - Horizontally Scrollable */}
      <div className="flex-1 overflow-y-auto overflow-x-auto">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-64">
            <Loader2 size={40} className="text-emerald-500 animate-spin mb-4" />
            <p className="text-zinc-500">Loading rounds...</p>
          </div>
        ) : rounds.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center px-4">
            <MapPin size={48} className="text-zinc-300 mb-4" />
            <h3 className="text-lg font-semibold text-zinc-700 mb-2">No Rounds Yet</h3>
            <p className="text-zinc-500 mb-4">Create your first round to organize your customers.</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-emerald-500 text-white font-medium rounded-xl hover:bg-emerald-600"
            >
              Add Your First Round
            </button>
          </div>
        ) : (
          <div className="min-w-max">
            {rounds.map(round => (
              <RoundRow 
                key={round.id} 
                round={round} 
                onClick={setSelectedRound}
                showAdhocOnly={showAdhocOnly}
                onEdit={setEditingRound}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Totals Footer - Squeegee Style */}
      {rounds.length > 0 && (
        <div className="bg-zinc-800 text-white flex-shrink-0 overflow-x-auto pb-safe">
          <div className="flex items-center min-w-max">
            <div className="w-40 sm:w-56 flex-shrink-0 px-3 sm:px-4 py-4 sticky left-0 bg-zinc-800 z-10">
              <p className="font-bold text-base sm:text-lg">TOTALS</p>
              <p className="text-zinc-400 text-xs sm:text-sm">
                {showAdhocOnly 
                  ? `${totals.adhocJobCount} ad-hoc jobs`
                  : `${totals.totalCustomers} customers · ${totals.totalJobs} jobs`
                }
              </p>
            </div>
            {showAdhocOnly ? (
              <div className="w-32 sm:w-40 flex-shrink-0 px-3 sm:px-4 py-4 text-right">
                <p className="font-bold text-base sm:text-lg text-amber-400">£{totals.adhocThisYear.toFixed(2)}</p>
                <p className="text-zinc-400 text-[10px] sm:text-xs">Done in {new Date().getFullYear()}</p>
              </div>
            ) : (
              <>
                <div className="w-24 sm:w-36 flex-shrink-0 px-2 sm:px-4 py-4 text-right">
                  <p className="font-bold text-sm sm:text-lg text-emerald-400">£{totals.monthlyValue.toFixed(2)}</p>
                  <p className="text-zinc-400 text-[10px] sm:text-xs">Avg Monthly</p>
                </div>
                <div className="w-28 sm:w-40 flex-shrink-0 px-2 sm:px-4 py-4 text-right">
                  <p className="font-bold text-sm sm:text-lg text-blue-400">£{totals.annualValue.toFixed(2)}</p>
                  <p className="text-zinc-400 text-[10px] sm:text-xs">Annual</p>
                </div>
                <div className="w-24 sm:w-36 flex-shrink-0 px-2 sm:px-4 py-4 text-right">
                  <p className={`font-bold text-sm sm:text-lg ${totals.adhocTotal > 0 ? 'text-amber-400' : 'text-zinc-500'}`}>
                    £{totals.adhocTotal.toFixed(2)}
                  </p>
                  <p className="text-zinc-400 text-[10px] sm:text-xs">Ad-hoc</p>
                </div>
              </>
            )}
            <div className="w-8 sm:w-12 flex-shrink-0"></div>
          </div>
        </div>
      )}
      
      {/* FAB - Fixed bottom LEFT */}
      <button 
        data-testid="add-round-fab"
        onClick={() => setShowAddModal(true)}
        className="fixed bottom-24 left-6 w-14 h-14 bg-emerald-500 rounded-full shadow-lg flex items-center justify-center text-white hover:bg-emerald-600 transition-colors z-40"
      >
        <Plus size={28} />
      </button>
      
      {/* Modals */}
      <AddRoundModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onSave={handleAddRound} />
      <EditRoundModal round={editingRound} onClose={() => setEditingRound(null)} onSave={handleEditRound} />
      {selectedRound && (
        <RoundCustomersPanel 
          round={selectedRound} 
          onClose={() => setSelectedRound(null)} 
          companyLogo={companyLogo}
          onEditRound={setEditingRound}
          onDeleteRound={handleDeleteRound}
        />
      )}
    </div>
  );
};

export default Rounds;
